import React, { useEffect } from 'react';

import type { LinkElement } from '@aline/common';
import { isEmpty } from 'ramda';
import { Node } from 'slate';
import type { NodeEntry } from 'slate';
import { Queries } from 'src/editor/queries';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useFocused } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';

import { LinkForm, LinkFormType } from './LinkForm';
import { editingLinkCMPath } from './LinkPopoverContent';

interface EditLinkFormProps {
  linkEntry: NodeEntry<LinkElement>;
}

export function EditLinkForm({ linkEntry: [link, path] }: EditLinkFormProps): JSX.Element | null {
  const editor = useEditor();
  const isEditorFocused = useFocused();
  const currentUserId = useCurrentUserId();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { commenting, editingMode, viewing } = useEditingMode();
  const initialText = Node.string(link);
  const initialHref = Queries.activeLinkURL(link);

  useEffect(() => {
    if (isEditorFocused) editingLinkCMPath(null);
  }, [isEditorFocused]);

  function onSubmit(href: string, options: { modifiedLinkText?: string } = {}): void {
    const { modifiedLinkText } = options;

    if (!currentUserId || !editingMode) return;

    if (href.length > 0) {
      const hrefChanged = href !== initialHref;

      // We want to allow an empty string through to the transforms so we use isEmpty here.
      if ((modifiedLinkText || isEmpty(modifiedLinkText)) && hrefChanged) {
        AuthorizedTransforms.updateLink(editor, {
          authorId: currentUserId,
          editingMode,
          linkPath: path,
          modifiedLinkText,
          newHref: href,
        });
      } else if ((modifiedLinkText || isEmpty(modifiedLinkText)) && !hrefChanged) {
        AuthorizedTransforms.updateLink(editor, {
          authorId: currentUserId,
          editingMode,
          linkPath: path,
          modifiedLinkText,
        });
      } else if (hrefChanged) {
        AuthorizedTransforms.updateLink(editor, {
          authorId: currentUserId,
          editingMode,
          linkPath: path,
          newHref: href,
        });
      }
    }
  }

  if (viewing || commenting) return null;

  return (
    <LinkForm
      closeLinkEditor={() => editingLinkCMPath(null)}
      initialHref={initialHref}
      initialText={initialText}
      type={LinkFormType.EDIT_LINK}
      onSubmit={onSubmit}
    />
  );
}
